/**
 * ARRAY-related reducers — kept in a separate file to avoid growing
 * `geometryReducer.ts` above the 2000-line soft limit.
 *
 * ## Semantics (AutoCAD-style non-associative ARRAY)
 *
 * AutoCAD's `-ARRAY` / `ARRAYCLASSIC` (the non-associative legacy
 * behaviour) produces a grid of individual entity copies. The source
 * objects stay in place at the (row 0, column 0) cell; the other
 * (rowCount * columnCount - 1) cells are populated with clones of each
 * selected entity, translated by the accumulated row/column offset.
 *
 * The column direction is rotated by `axisAngle` degrees (positive =
 * CCW, AutoCAD convention). Rows are perpendicular to the column
 * direction.
 *
 * ARRAYPOLAR distributes entities around a center point. The first
 * item (i=0) is the source in place. The remaining `itemCount-1`
 * items are rotated around `centerPoint` by `i * fillAngle /
 * itemCount` degrees. With `rowCount > 1`, concentric rings are
 * generated at radial offsets of `rowSpacing` from the original
 * radius R0 = |centerPoint - sourceReferencePoint|. If
 * `rotateItems` is true, each copy's internal angle (Text/MText
 * rotation, Arc startAngle/endAngle, Hatch arc angles) is also
 * rotated — matching AutoCAD's default behaviour.
 *
 * A hard cap of 100,000 copies mirrors AutoCAD's `MAXARRAY` registry
 * variable — exceeding it is a no-op with a console warning rather
 * than a silent giant edit.
 *
 * ## Function dependency map
 *
 * handleCreateArrayRect(doc, command)
 *   -> iterates rowCount x columnCount grid (skips (0,0))
 *   -> for each source entity, transformEntityCoords(entity, translate)
 *   -> idAllocator.next(typePrefix)            // once-seeded, amortized O(1)
 *   -> appendGeometryEntities(doc, clones)     // ONE assemble for the batch
 *
 * handleCreateArrayPolar(doc, command)
 *   -> entityReferencePoint(sourceEntities[0]) -> R0 from centerPoint
 *   -> iterates rowCount x itemCount rings (skips ring=0, i=0 — origin)
 *   -> for each source entity:
 *        if ring > 0: translate radially outward by (R - R0)
 *        rotatePoint(center, angleDeg) for coords
 *        if rotateItems: rotateEntityAngles(angleDeg)
 *   -> idAllocator.next(typePrefix)            // once-seeded, amortized O(1)
 *   -> appendGeometryEntities(doc, clones)     // ONE assemble for the batch
 *
 * T6a perf: the create-path was O(N²) — nextGeometryEntityId + an
 * assembleDrawingDocument validation PER clone inside the grid loop. The
 * allocator (createEntityIdAllocator) reproduces the exact id sequence in
 * amortized O(1), and appendGeometryEntities validates ONCE for the batch →
 * O(N) overall. Byte-identical to the old per-entity loop.
 */

import {
  computeOcsAxes,
  type DrawingDocument,
  type DrawingEntity,
  ocsToWcs,
  polylineSubtype,
} from "@dxf-ai-editor/drawing-model";
import {
  arrayPathPlaneRefusal,
  type ArrayPathPlaneRefusal,
  heavyPolylineCurveRefusal,
  isSupportedHeavyPolylineCurve,
  type PathEntity,
  type PathSample,
  pathTotalLength,
  rotateEntityAngles,
  rotatePoint,
  samplePathDivide,
  samplePathMeasure,
  transformEntityCoords,
  translatePoint,
} from "@dxf-ai-editor/command-engine";
import type { EditorCommand } from "@dxf-ai-editor/contracts";

import {
  appendGeometryEntities,
  cloneEntityWithFreshIdentity,
  createEntityIdAllocator,
  type EntityIdAllocator,
} from "./geometryReducerHelpers";

type CreateArrayRectCommand = Extract<
  EditorCommand,
  { type: "geometry/create-arrayrect" }
>;

type CreateArrayPolarCommand = Extract<
  EditorCommand,
  { type: "geometry/create-arraypolar" }
>;

type CreateArrayPathCommand = Extract<
  EditorCommand,
  { type: "geometry/create-arraypath" }
>;

type ArrayEditCommand = Extract<EditorCommand, { type: "geometry/arrayedit" }>;

type Point2D = { x: number; y: number };

const MAX_ARRAY_ITEMS = 100_000;

/** Narrow string union of supported array modes. Matches the Zod enum in `drawingEntityBaseSchema.arrayMode`. */
type ArrayMode = "rect" | "polar" | "path";

/**
 * Mark every source entity in `sourceIds` with `arrayGroupId`, the
 * `arraySourceOfGroup` marker, and the `arrayMode` tag, producing a
 * new DrawingDocument. Used by all three array creation handlers.
 */
function tagSourceEntities(
  doc: DrawingDocument,
  sourceIds: readonly string[],
  groupId: string,
  mode: ArrayMode,
): DrawingDocument {
  const idSet = new Set(sourceIds);
  return {
    ...doc,
    spaces: doc.spaces.map((space) => ({
      ...space,
      entities: space.entities.map((entity) =>
        idSet.has(entity.id)
          ? {
              ...entity,
              arrayGroupId: groupId,
              arraySourceOfGroup: groupId,
              arrayMode: mode,
            }
          : entity,
      ),
    })),
  };
}

/**
 * Returns `true` when tagging `sourceIds` with `groupId` would MERGE the
 * new array into a PRE-EXISTING, unrelated array group — i.e. the document
 * already contains an entity tagged `arrayGroupId === groupId` that is NOT
 * one of the entities being arrayed (`sourceIds`).
 *
 * There is no separate array-group registry: a group's membership is
 * defined solely by the `arrayGroupId` tag on its entities (see
 * `handleArrayEdit` / `countArrayGroupMembers`). A caller-supplied
 * `arrayGroupId` (e.g. an AI `ARRAYRECT` that sets it explicitly — the
 * field is an OPTIONAL override in `arrayRectAiSchema`) therefore COLLIDES
 * with an existing group when any foreign (non-`sourceIds`) entity already
 * carries the tag. Were the merge allowed, a later ARRAYEDIT/delete on that
 * `groupId` would hit BOTH arrays (it scans every entity with the tag).
 *
 * ARRAYEDIT legitimately RE-USES a groupId whose only tagged members ARE
 * its own surviving sources — which it passes as `sourceIds` after deleting
 * the copies — so that case is NOT a collision and is allowed through. A
 * fresh creation with a never-seen id likewise finds no tagged entity and
 * passes.
 */
function arrayGroupIdCollidesWithForeign(
  doc: DrawingDocument,
  groupId: string,
  sourceIds: readonly string[],
): boolean {
  const sourceSet = new Set(sourceIds);
  for (const space of doc.spaces) {
    for (const entity of space.entities) {
      if (entity.arrayGroupId === groupId && !sourceSet.has(entity.id)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Default cell dimensions for ARRAY creation — used when the user picks
 * a bounding rectangle without explicit row/column counts. Values match
 * AutoCAD's legacy `-ARRAY` defaults: 96 wide by 72 tall drawing units.
 */
export const DEFAULT_ARRAY_CELL_WIDTH = 96;
export const DEFAULT_ARRAY_CELL_HEIGHT = 72;

/**
 * Collect the source entities referenced by `entityIds` once, walking
 * every space in the document. Used by all three ARRAY command handlers
 * to avoid O(spaces × entities × entityIds) lookups per iteration of
 * the outer loop. Order of returned entities is the order they appear
 * in `doc.spaces` (not the order of `entityIds`), consistent with the
 * existing non-associative ARRAY reducer semantics.
 */
function collectSourceEntities(
  doc: DrawingDocument,
  entityIds: readonly string[],
): DrawingEntity[] {
  const idSet = new Set(entityIds);
  const sourceEntities: DrawingEntity[] = [];
  for (const space of doc.spaces) {
    for (const entity of space.entities) {
      if (idSet.has(entity.id)) {
        sourceEntities.push(entity);
      }
    }
  }
  return sourceEntities;
}

/**
 * Stamp one array-copy clone and push it onto `clones` (T6a one-truth, shared
 * by all three ARRAY{RECT,POLAR,PATH} expansion loops).
 *
 * Mints the clone id from the once-seeded {@link createEntityIdAllocator}
 * (amortized O(1), reproducing `nextGeometryEntityId`'s exact sequence), then
 * stamps the array-membership fields: copies carry `arrayGroupId` + `arrayMode`
 * but DROP `arraySourceOfGroup` — that marker is how ARRAYEDIT distinguishes
 * sources (to keep) from copies (to delete + regenerate). The id prefix is
 * `entity.type.toLowerCase().replace(/\s+/g, "-")`, identical to the prior
 * per-loop code, so the minted ids stay byte-identical.
 */
function pushArrayClone(
  clones: DrawingEntity[],
  idAllocator: EntityIdAllocator,
  transformed: DrawingEntity,
  groupId: string,
  mode: ArrayMode,
): void {
  const typePrefix = transformed.type.toLowerCase().replace(/\s+/g, "-");
  const newId = idAllocator.next(typePrefix);
  const { arraySourceOfGroup: _unused, ...copyBase } = transformed;
  clones.push({
    // T9b review round 3 — fresh own-record identity (no shared seqendHandle).
    ...cloneEntityWithFreshIdentity(copyBase as DrawingEntity, newId),
    arrayGroupId: groupId,
    arrayMode: mode,
  });
}

/**
 * Handle `geometry/create-arrayrect` — produces a rectangular grid of
 * non-associative copies of the given source entities. The source
 * objects are unchanged (they occupy the origin cell (0, 0)); copies
 * are appended for every other cell in the grid.
 *
 * Returns the document unchanged when the total number of copies
 * (rowCount × columnCount × entityIds.length) exceeds the
 * `MAX_ARRAY_ITEMS` (100,000) limit — this is a safety guard that
 * matches AutoCAD's `MAXARRAY` behaviour.
 */
export function handleCreateArrayRect(
  doc: DrawingDocument,
  command: CreateArrayRectCommand,
): DrawingDocument {
  const {
    entityIds,
    rowCount,
    columnCount,
    rowSpacing,
    columnSpacing,
    axisAngle,
  } = command;

  // AutoCAD MAXARRAY check (default 100,000)
  const totalItems = rowCount * columnCount * entityIds.length;
  if (totalItems > MAX_ARRAY_ITEMS) {
    console.warn(
      `ARRAYRECT: requested ${totalItems} items exceeds MAXARRAY limit of ${MAX_ARRAY_ITEMS}; operation skipped.`,
    );
    return doc;
  }

  const angleRad = (axisAngle * Math.PI) / 180;
  const cosA = Math.cos(angleRad);
  const sinA = Math.sin(angleRad);

  const sourceEntities = collectSourceEntities(doc, entityIds);
  if (sourceEntities.length === 0) {
    return doc;
  }

  const groupId = command.arrayGroupId;
  // Reject a caller-supplied arrayGroupId that already names a different
  // array group (fail-closed, matching the MAXARRAY / empty-source guards).
  // Without this an AI-reused id would merge unrelated arrays so a later
  // ARRAYEDIT/delete on the group would corrupt both.
  if (arrayGroupIdCollidesWithForeign(doc, groupId, entityIds)) {
    console.warn(
      `ARRAYRECT: arrayGroupId "${groupId}" already names a different array group; operation skipped to avoid merging unrelated arrays.`,
    );
    return doc;
  }
  const taggedDoc = tagSourceEntities(doc, entityIds, groupId, "rect");

  // Re-collect source entities AFTER tagging so the tag-propagated
  // fields (arrayGroupId, arraySourceOfGroup, arrayMode) are on the cloned copies.
  const taggedSourceEntities = collectSourceEntities(taggedDoc, entityIds);

  // T6a perf: mint clone ids via a once-seeded allocator (amortized O(1),
  // reproduces nextGeometryEntityId's exact sequence) and append the whole grid
  // with ONE assembleDrawingDocument pass instead of O(N) per clone (O(N²) over
  // the grid). Seeded from the TAGGED doc — the same doc the per-entity loop
  // minted ids against (tagging mutates fields, not the entity count).
  const idAllocator = createEntityIdAllocator(taggedDoc);
  const clones: DrawingEntity[] = [];

  for (let row = 0; row < rowCount; row++) {
    for (let col = 0; col < columnCount; col++) {
      // The origin cell keeps the source objects in place.
      if (row === 0 && col === 0) continue;

      // Column direction rotated by axisAngle; rows perpendicular.
      const dx = col * columnSpacing * cosA - row * rowSpacing * sinA;
      const dy = col * columnSpacing * sinA + row * rowSpacing * cosA;

      for (const entity of taggedSourceEntities) {
        const translated = transformEntityCoords(entity, (p) =>
          translatePoint(p, dx, dy),
        );
        pushArrayClone(clones, idAllocator, translated, groupId, "rect");
      }
    }
  }

  return appendGeometryEntities(taggedDoc, clones);
}

/**
 * Utility — kept exported for tests. Computes the number of copies
 * that would be produced by a given command (excluding the source
 * objects themselves).
 */
export function countArrayRectCopies(
  rowCount: number,
  columnCount: number,
  entityCount: number,
): number {
  return Math.max(0, rowCount * columnCount - 1) * entityCount;
}

/**
 * Derive a stable WCS reference point for a DrawingEntity — used by
 * ARRAYPOLAR to measure the base radius R0 between the entity and the polar
 * center, and thereby the RADIAL direction each outer ring is offset along.
 * Each entity type exposes the canonical "origin" point that best describes
 * where it sits in the drawing (start of line, center of circle/arc/ellipse,
 * insertion point of text/block/table, first vertex of a polyline, …).
 *
 * ## The switch is EXHAUSTIVE, deliberately
 *
 * @codex PR #785 round 13, FINDING 2 — this used to end in a `default:` arm
 * returning `{ x: 0, y: 0 }`, and every union member the arms did not name
 * fell into it SILENTLY. That is not a harmless fallback: `handleCreateArrayPolar`
 * measures `R0 = |centerPoint − refPoint|` and then offsets ring `r` by
 * `(R − R0)` along `(refPoint − centerPoint) / R0`, so a wrong reference point
 * sends the outer rings off in the wrong DIRECTION. With a `Table` at
 * (100, 100), a centre of (0, 0) and `rowSpacing = 50`, the fallback made
 * `R0 = 0`, the radial unit vector degenerate to +X, and row 1 landed at
 * (150, 100) instead of the radial (135.36, 135.36). Seven members were in
 * that state — `Point`, `Ellipse`, `Spline`, `Wipeout` from the original
 * switch, plus `Polyline` / `Table` / `OLE Frame` admitted later.
 *
 * The `never` check below is what stops the class from coming back: the next
 * kind added to `DrawingEntity` is a TYPE ERROR here rather than another
 * silent (0, 0). Same discipline `computeEntityWcsBounds`
 * (`drawing-model/wcsBounds.ts`) adopted for the identical reason.
 *
 * ## WCS, not the raw field
 *
 * A heavy 2D `POLYLINE` stores its vertices in the entity's OCS (DXF group
 * 210), so the raw `location` of a tilted one is NOT where it sits in the
 * world. It is lifted through the repo's one-truth Arbitrary Axis Algorithm
 * (`drawing-model`'s {@link computeOcsAxes} / {@link ocsToWcs}, the same pair
 * `wcsBoundsLeaf.heavyPolylineLocal`'s bounds finalize uses) so the radius is
 * measured against the position the drawing actually shows. A 3D polyline and
 * both mesh subtypes carry real WCS vertices and are read verbatim.
 *
 * An empty vertex / point / seed list has no position to report and still
 * yields `{ x: 0, y: 0 }` — but now as a NAMED, per-kind answer to "this
 * entity carries no reference coordinate", not as a catch-all for kinds
 * nobody wrote an arm for.
 */
export function entityReferencePoint(entity: DrawingEntity): Point2D {
  switch (entity.type) {
    case "Line":
      return { x: entity.start.x, y: entity.start.y };
    case "Point":
      return { x: entity.location.x, y: entity.location.y };
    case "Circle":
    case "Arc":
      return { x: entity.center.x, y: entity.center.y };
    case "Ellipse":
      return { x: entity.center.x, y: entity.center.y };
    case "Spline": {
      // The defining geometry: control points when the spline has them, else
      // the fit points a fit-only SPLINE carries instead (ezdxf `Spline`
      // exposes both and a file may legitimately have only one).
      const anchor = entity.controlPoints[0] ?? entity.fitPoints[0];
      return anchor ? { x: anchor.x, y: anchor.y } : { x: 0, y: 0 };
    }
    case "LWPOLYLINE":
      return entity.vertices.length > 0
        ? { x: entity.vertices[0].x, y: entity.vertices[0].y }
        : { x: 0, y: 0 };
    case "Text":
    case "MText":
    case "Block":
      return { x: entity.insertionPoint.x, y: entity.insertionPoint.y };
    case "Dimension":
      return { x: entity.defPoint.x, y: entity.defPoint.y };
    case "Multileader":
      return { x: entity.textLocation.x, y: entity.textLocation.y };
    case "Wipeout":
      return entity.vertices.length > 0
        ? { x: entity.vertices[0].x, y: entity.vertices[0].y }
        : { x: 0, y: 0 };
    case "Hatch":
      return entity.seedPoints.length > 0
        ? { x: entity.seedPoints[0].x, y: entity.seedPoints[0].y }
        : { x: 0, y: 0 };
    case "Rectangular Associative Array":
      return { x: entity.basePoint.x, y: entity.basePoint.y };
    case "Revision Cloud":
      return entity.vertices.length > 0
        ? { x: entity.vertices[0].x, y: entity.vertices[0].y }
        : { x: 0, y: 0 };
    case "Viewport":
      return { x: entity.center.x, y: entity.center.y };
    case "Polyline": {
      const first = entity.vertices[0];
      if (!first) return { x: 0, y: 0 };
      if (polylineSubtype(entity) !== "2d") {
        return { x: first.location.x, y: first.location.y };
      }
      // A 2D polyline's plane travels on the `elevation` dummy point's Z, and
      // its vertices' own Z is not geometric — the same split
      // `wcsBoundsLeaf.heavyPolylineLocal` makes before it lifts.
      const wcs = ocsToWcs(
        { x: first.location.x, y: first.location.y, z: entity.elevation.z ?? 0 },
        computeOcsAxes(entity.extrusion),
      );
      return { x: wcs.x, y: wcs.y };
    }
    case "Table":
      // `ACAD_TABLE` is placed by its insertion point (10/20/30, WCS) exactly
      // like the block reference it is in DXF — same arm shape as `Block`.
      return { x: entity.insertionPoint.x, y: entity.insertionPoint.y };
    case "OLE Frame":
      // An `OLE2FRAME` stores two opposite WCS corners and no insertion point;
      // the upper-left one (10/20/30) is the record's own first point, which is
      // what `oleFrameLocal` reads first too.
      return { x: entity.upperLeft.x, y: entity.upperLeft.y };
    default: {
      const exhaustive: never = entity;
      throw new Error(
        `entityReferencePoint: unhandled entity type ${String((exhaustive as { type?: string }).type)}`,
      );
    }
  }
}

/**
 * Handle `geometry/create-arraypolar` — produces a polar (circular)
 * distribution of non-associative copies of the source entities around
 * a center point. Supports concentric radial rings via `rowCount` /
 * `rowSpacing`.
 *
 * Algorithm:
 * 1. Derive `R0` = distance from `centerPoint` to the first source
 *    entity's reference point.
 * 2. For each ring r in [0, rowCount):
 *    - Let R = R0 + r * rowSpacing.
 *    - For each item i in [0, itemCount):
 *      - Skip the origin copy (ring 0, item 0) — source stays put.
 *      - Compute angleDeg = i * (fillAngle / itemCount).
 *      - For each source entity:
 *        - If ring > 0, translate radially outward by (R - R0).
 *        - Rotate all coords around `centerPoint` by angleDeg.
 *        - If rotateItems, rotate internal angle fields by angleDeg.
 *      - Append clone with a fresh ID.
 *
 * Returns the document unchanged when the total number of items
 * (`itemCount * rowCount * entityIds.length`) exceeds the
 * `MAX_ARRAY_ITEMS` (100,000) limit.
 */
export function handleCreateArrayPolar(
  doc: DrawingDocument,
  command: CreateArrayPolarCommand,
): DrawingDocument {
  const {
    entityIds,
    centerPoint,
    itemCount,
    fillAngle,
    rotateItems,
    rowCount,
    rowSpacing,
  } = command;

  // AutoCAD MAXARRAY check (default 100,000)
  const totalItems = itemCount * rowCount * entityIds.length;
  if (totalItems > MAX_ARRAY_ITEMS) {
    console.warn(
      `ARRAYPOLAR: requested ${totalItems} items exceeds MAXARRAY limit of ${MAX_ARRAY_ITEMS}; operation skipped.`,
    );
    return doc;
  }

  const sourceEntities = collectSourceEntities(doc, entityIds);
  if (sourceEntities.length === 0) {
    return doc;
  }

  // Compute R0 from the first source entity's reference point. When
  // the reference point coincides with the polar center (R0 = 0), the
  // radial direction is undefined — we arbitrarily use (1, 0) so that
  // rings still spread out along the +X axis (matching ezdxf/ACAD's
  // default behaviour for this degenerate case).
  const refPoint = entityReferencePoint(sourceEntities[0]);
  const dx0 = refPoint.x - centerPoint.x;
  const dy0 = refPoint.y - centerPoint.y;
  const R0 = Math.hypot(dx0, dy0);
  const radialDirX = R0 > 1e-10 ? dx0 / R0 : 1;
  const radialDirY = R0 > 1e-10 ? dy0 / R0 : 0;

  const anglePerItem = fillAngle / itemCount;

  const groupId = command.arrayGroupId;
  // Reject a caller-supplied arrayGroupId that already names a different
  // array group (fail-closed, matching the MAXARRAY / empty-source guards).
  if (arrayGroupIdCollidesWithForeign(doc, groupId, entityIds)) {
    console.warn(
      `ARRAYPOLAR: arrayGroupId "${groupId}" already names a different array group; operation skipped to avoid merging unrelated arrays.`,
    );
    return doc;
  }
  const taggedDoc = tagSourceEntities(doc, entityIds, groupId, "polar");

  // Re-collect source entities AFTER tagging.
  const taggedSourceEntities = collectSourceEntities(taggedDoc, entityIds);

  // T6a perf: once-seeded allocator + single batch append (see handleCreateArrayRect).
  const idAllocator = createEntityIdAllocator(taggedDoc);
  const clones: DrawingEntity[] = [];

  for (let ring = 0; ring < rowCount; ring++) {
    const radialOffset = ring * rowSpacing;

    for (let i = 0; i < itemCount; i++) {
      // Origin copy: source stays in place at ring 0, item 0.
      if (ring === 0 && i === 0) continue;

      const angleDeg = i * anglePerItem;

      for (const entity of taggedSourceEntities) {
        // Step 1: translate outward along radial direction (ring > 0).
        let transformed: DrawingEntity = entity;
        if (radialOffset !== 0) {
          const dx = radialOffset * radialDirX;
          const dy = radialOffset * radialDirY;
          transformed = transformEntityCoords(transformed, (p) =>
            translatePoint(p, dx, dy),
          );
        }

        // Step 2: rotate coords around centerPoint by angleDeg.
        if (angleDeg !== 0) {
          transformed = transformEntityCoords(transformed, (p) =>
            rotatePoint(p, centerPoint, angleDeg),
          );

          // Step 3: rotate internal angle fields (Text/MText/Arc/Hatch).
          if (rotateItems) {
            transformed = rotateEntityAngles(transformed, angleDeg);
          }
        }

        pushArrayClone(clones, idAllocator, transformed, groupId, "polar");
      }
    }
  }

  return appendGeometryEntities(taggedDoc, clones);
}

/**
 * Utility — kept exported for tests. Computes the number of copies
 * that would be produced by a given ARRAYPOLAR command (excluding the
 * original source entities which occupy ring 0, item 0).
 */
export function countArrayPolarCopies(
  itemCount: number,
  rowCount: number,
  entityCount: number,
): number {
  return Math.max(0, itemCount * rowCount - 1) * entityCount;
}

/** Exported for tests only. */
export const ARRAYRECT_MAX_ITEMS = MAX_ARRAY_ITEMS;
export const ARRAYPOLAR_MAX_ITEMS = MAX_ARRAY_ITEMS;
export const ARRAYPATH_MAX_ITEMS = MAX_ARRAY_ITEMS;

// ── ARRAYPATH ───────────────────────────────────────────────────────

/**
 * Narrow a DrawingEntity to a PathEntity (Line / Circle / Arc / LWPOLYLINE /
 * heavy 2D `POLYLINE`) — the entity types supported as ARRAYPATH path curves.
 *
 * The heavy `POLYLINE` arm is @codex PR #785 round 14, "Accept 2D heavy
 * polylines as ARRAYPATH curves": a visible heavy polyline — including one
 * whose bulged path the round-3 topology code already resolves — could not
 * drive ARRAYPATH at all, because this guard knew only the lightweight family
 * and the interactive picker's own type set ended at `LWPOLYLINE`.
 *
 * WHICH heavy subtype is not decided here: `isSupportedHeavyPolylineCurve`
 * (`command-engine/heavyPolylineCurveRefusal.ts`) is the ONE eligibility truth
 * this guard, the picker gate and the shell reducer's RU+EN audit row all read,
 * so a path the picker offers can never be one this reducer silently drops. A
 * polygon / poly-face mesh is declined because it is not a curve at all (which
 * is AutoCAD's own verdict), and a 3D polyline because this package's sampler is
 * 2D end to end — both reported to the user, never silently skipped.
 */
export function isPathEntity(entity: DrawingEntity): entity is PathEntity {
  return (
    entity.type === "Line" ||
    entity.type === "Circle" ||
    entity.type === "Arc" ||
    entity.type === "LWPOLYLINE" ||
    isSupportedHeavyPolylineCurve(entity, "array-path")
  );
}

/**
 * Handle `geometry/create-arraypath` — distributes non-associative
 * copies of the source entities along the given path entity.
 *
 * Algorithm:
 * 1. Find the path entity by `pathEntityId` across all spaces.
 * 2. Validate that the path is a Line / Circle / Arc / LWPOLYLINE /
 *    supported heavy 2D POLYLINE ({@link isPathEntity}) AND that its curve is
 *    confined to a plane parallel to WCS XY (`arrayPathPlaneRefusal`) — the
 *    subtype half and the frame half of one eligibility question, each its own
 *    `command-engine` truth.
 * 3. Collect source entities (order preserved from `entityIds`).
 * 4. Compute samples along the path:
 *    - "divide": N evenly-spaced samples (both endpoints included)
 *    - "measure": samples every `itemSpacing` arc-length (first at 0)
 * 5. Use `entityReferencePoint(sourceEntities[0])` as the base point.
 *    Each sample defines the target placement of this reference point.
 * 6. For every sample except the first (which corresponds to the
 *    source's original position), translate each source entity by
 *    (sample.point - basePoint). If `alignItems`, rotate the clone
 *    around `sample.point` by `sample.tangentAngle` and rotate the
 *    entity's internal angle fields (Text rotation, Arc angles, etc.).
 *
 * Step 6 mixes a SAMPLE with `basePoint` and with the clones' own stored
 * geometry, so both sides have to be in the same frame. They are: `PathSample`
 * is documented WCS for every path kind, and `command-engine/pathSampling.ts`
 * re-expresses each sample out of the path entity's group-210 OCS plane before
 * returning it (@codex PR #785 round 15, P2 — before that a mirrored-extrusion
 * path sent its copies the opposite way from the curve they follow). This
 * reducer therefore does no frame math of its own; adding any here would be a
 * second, drifting answer to a question `command-engine` already owns.
 *
 * And where the sample's frame CANNOT be reduced to that pair — a path whose
 * curve leaves WCS XY — the command declines rather than placing copies along
 * the curve's plan shadow (@codex PR #785 round 16, P2). See the guard below.
 *
 * MaxArray: `samples.length * entityIds.length` must not exceed
 * `MAX_ARRAY_ITEMS` (100,000). Otherwise returns the document
 * unchanged with a console warning.
 *
 * Returns the document unchanged if the path entity cannot be found
 * or is of an unsupported type, or if the method-specific parameter
 * is missing (itemCount for divide, itemSpacing for measure).
 */
export function handleCreateArrayPath(
  doc: DrawingDocument,
  command: CreateArrayPathCommand,
): DrawingDocument {
  const {
    entityIds,
    pathEntityId,
    method,
    itemCount,
    itemSpacing,
    alignItems,
  } = command;

  // Locate path entity.
  let pathEntity: DrawingEntity | null = null;
  for (const space of doc.spaces) {
    for (const e of space.entities) {
      if (e.id === pathEntityId) {
        pathEntity = e;
        break;
      }
    }
    if (pathEntity) break;
  }
  if (!pathEntity) {
    console.warn(
      `ARRAYPATH: path entity "${pathEntityId}" not found; operation skipped.`,
    );
    return doc;
  }
  // Read BEFORE the narrowing below, not inside it: `isPathEntity` is a type
  // PREDICATE, so in its negative branch TypeScript has already removed
  // `PolylineEntity` from the union — even though a DECLINED heavy-polyline
  // subtype is exactly what reaches that branch at runtime.
  const heavyRefusal =
    pathEntity.type === "Polyline"
      ? heavyPolylineCurveRefusal(pathEntity, "array-path")
      : null;
  if (!isPathEntity(pathEntity)) {
    // A heavy POLYLINE reaches this arm only for a SUBTYPE the command
    // declines, so the reason is NAMED rather than reported as "unsupported
    // type Polyline" — which would read as "the editor does not know heavy
    // polylines" when it does. The user-facing half of the same verdict is the
    // RU+EN warning-tone audit row `geometryShellReducer.ts` raises from the
    // SAME predicate.
    const reason =
      heavyRefusal !== null
        ? `heavy POLYLINE subtype declined (${heavyRefusal})`
        : `unsupported type "${pathEntity.type}"`;
    console.warn(
      `ARRAYPATH: path entity "${pathEntityId}" has ${reason}; operation skipped.`,
    );
    return doc;
  }

  // The FRAME half of the same eligibility, asked AFTER the subtype half and
  // from the same shape (@codex PR #785 round 16, P2 — "Preserve Z when
  // sampling tilted array paths"). `PathSample.point` is `{x, y}` and the
  // placement below is a 2D `translatePoint`, so a path whose curve leaves the
  // WCS XY plane would be followed only by its plan SHADOW: foreshortened where
  // the plane leans, and — for extrusion `(0, 1, 0)`, whose OCS Y axis IS WCS Z
  // — collapsed onto one point, stacking every copy on the source. That is a
  // confidently wrong array rather than a missing one, so the command declines
  // it, exactly as it already declines a 3D heavy polyline for the same gap.
  // `command-engine/arrayPathPlaneRefusal.ts` is the single truth for WHICH
  // frames; this reducer adds no plane math of its own.
  const planeRefusal: ArrayPathPlaneRefusal | null =
    arrayPathPlaneRefusal(pathEntity);
  if (planeRefusal !== null) {
    console.warn(
      `ARRAYPATH: path entity "${pathEntityId}" is not confined to a plane parallel to WCS XY (${planeRefusal}); operation skipped.`,
    );
    return doc;
  }

  const sourceEntities = collectSourceEntities(doc, entityIds);
  if (sourceEntities.length === 0) {
    return doc;
  }

  // Compute samples along the path.
  let samples: PathSample[];
  if (method === "divide") {
    const count = itemCount ?? 0;
    if (count < 2) {
      console.warn(
        `ARRAYPATH: divide method requires itemCount >= 2 (got ${count}); operation skipped.`,
      );
      return doc;
    }
    samples = samplePathDivide(pathEntity, count);
  } else {
    const spacing = itemSpacing ?? 0;
    if (spacing <= 0) {
      console.warn(
        `ARRAYPATH: measure method requires itemSpacing > 0 (got ${spacing}); operation skipped.`,
      );
      return doc;
    }
    const totalLen = pathTotalLength(pathEntity);
    if (totalLen < spacing) {
      // Not enough path length to place a second copy — no-op.
      console.warn(
        `ARRAYPATH: path length (${totalLen}) is less than measure spacing (${spacing}); no copies created.`,
      );
      return doc;
    }
    samples = samplePathMeasure(pathEntity, spacing);
  }

  // AutoCAD MAXARRAY check.
  const totalItems = samples.length * entityIds.length;
  if (totalItems > MAX_ARRAY_ITEMS) {
    console.warn(
      `ARRAYPATH: requested ${totalItems} items exceeds MAXARRAY limit of ${MAX_ARRAY_ITEMS}; operation skipped.`,
    );
    return doc;
  }

  // Base point for placement = reference point of the first source entity.
  const basePoint = entityReferencePoint(sourceEntities[0]!);

  const groupId = command.arrayGroupId;
  // Reject a caller-supplied arrayGroupId that already names a different
  // array group (fail-closed, matching the MAXARRAY / empty-source guards).
  if (arrayGroupIdCollidesWithForeign(doc, groupId, entityIds)) {
    console.warn(
      `ARRAYPATH: arrayGroupId "${groupId}" already names a different array group; operation skipped to avoid merging unrelated arrays.`,
    );
    return doc;
  }
  const taggedDoc = tagSourceEntities(doc, entityIds, groupId, "path");

  // Re-collect source entities AFTER tagging.
  const taggedSourceEntities = collectSourceEntities(taggedDoc, entityIds);

  // T6a perf: once-seeded allocator + single batch append (see handleCreateArrayRect).
  const idAllocator = createEntityIdAllocator(taggedDoc);
  const clones: DrawingEntity[] = [];

  // Sample 0 corresponds to the source's "starting" position on the
  // path — we treat it as the origin copy (no new entity) to match
  // the ARRAYRECT / ARRAYPOLAR semantics where the source itself
  // represents one of the array items.
  for (let i = 1; i < samples.length; i++) {
    const sample = samples[i]!;
    const dx = sample.point.x - basePoint.x;
    const dy = sample.point.y - basePoint.y;

    for (const entity of taggedSourceEntities) {
      // 1. Translate source entity so its reference point lands on the
      //    sample point.
      let transformed: DrawingEntity = transformEntityCoords(entity, (p) =>
        translatePoint(p, dx, dy),
      );

      // 2. If alignItems, rotate the translated clone around the sample
      //    point by the path's tangentAngle at that sample.
      if (alignItems && sample.tangentAngle !== 0) {
        transformed = transformEntityCoords(transformed, (p) =>
          rotatePoint(p, sample.point, sample.tangentAngle),
        );
        transformed = rotateEntityAngles(transformed, sample.tangentAngle);
      }

      pushArrayClone(clones, idAllocator, transformed, groupId, "path");
    }
  }

  return appendGeometryEntities(taggedDoc, clones);
}

// ── ARRAYEDIT ───────────────────────────────────────────────────────

/**
 * Handle `geometry/arrayedit` — re-generate an existing array group
 * with new parameters. Finds all entities tagged with the target
 * `arrayGroupId`, preserves the sources (entities with
 * `arraySourceOfGroup === arrayGroupId`), deletes all copies
 * (entities with `arrayGroupId` but no `arraySourceOfGroup`), and
 * dispatches the appropriate create handler with the new parameters,
 * re-using the same groupId so the regenerated copies remain linked
 * to the same source entities.
 *
 * Returns the document unchanged if no entities match the groupId
 * (no-op) or if the mode/parameters are missing.
 */
export function handleArrayEdit(
  doc: DrawingDocument,
  command: ArrayEditCommand,
): DrawingDocument {
  const { arrayGroupId, mode } = command;

  // Collect source entity ids and check the group exists.
  const sourceIds: string[] = [];
  let hasAnyMember = false;
  for (const space of doc.spaces) {
    for (const entity of space.entities) {
      if (entity.arrayGroupId === arrayGroupId) {
        hasAnyMember = true;
        if (entity.arraySourceOfGroup === arrayGroupId) {
          sourceIds.push(entity.id);
        }
      }
    }
  }

  if (!hasAnyMember) {
    console.warn(
      `ARRAYEDIT: no entities found with arrayGroupId "${arrayGroupId}"; operation skipped.`,
    );
    return doc;
  }

  if (sourceIds.length === 0) {
    console.warn(
      `ARRAYEDIT: no source entities found for arrayGroupId "${arrayGroupId}"; operation skipped.`,
    );
    return doc;
  }

  // Delete all copies (entities with arrayGroupId but no arraySourceOfGroup).
  const withoutCopies: DrawingDocument = {
    ...doc,
    spaces: doc.spaces.map((space) => ({
      ...space,
      entities: space.entities.filter(
        (entity) =>
          entity.arrayGroupId !== arrayGroupId ||
          entity.arraySourceOfGroup === arrayGroupId,
      ),
    })),
  };

  // Dispatch the appropriate create handler with the new parameters,
  // re-using the same groupId.
  if (mode === "rect") {
    if (!command.rect) {
      console.warn(
        `ARRAYEDIT: mode="rect" requires rect parameters; operation skipped.`,
      );
      return doc;
    }
    const createCmd: CreateArrayRectCommand = {
      type: "geometry/create-arrayrect",
      source: command.source,
      entityIds: sourceIds,
      arrayGroupId,
      basePoint: { x: 0, y: 0 }, // basePoint is not used by reducer algorithm
      rowCount: command.rect.rowCount,
      columnCount: command.rect.columnCount,
      rowSpacing: command.rect.rowSpacing,
      columnSpacing: command.rect.columnSpacing,
      axisAngle: command.rect.axisAngle,
    };
    return handleCreateArrayRect(withoutCopies, createCmd);
  }

  if (mode === "polar") {
    if (!command.polar) {
      console.warn(
        `ARRAYEDIT: mode="polar" requires polar parameters; operation skipped.`,
      );
      return doc;
    }
    const createCmd: CreateArrayPolarCommand = {
      type: "geometry/create-arraypolar",
      source: command.source,
      entityIds: sourceIds,
      arrayGroupId,
      centerPoint: command.polar.centerPoint,
      itemCount: command.polar.itemCount,
      fillAngle: command.polar.fillAngle,
      rotateItems: command.polar.rotateItems,
      rowCount: command.polar.rowCount,
      rowSpacing: command.polar.rowSpacing,
    };
    return handleCreateArrayPolar(withoutCopies, createCmd);
  }

  if (mode === "path") {
    if (!command.path) {
      console.warn(
        `ARRAYEDIT: mode="path" requires path parameters; operation skipped.`,
      );
      return doc;
    }
    const createCmd: CreateArrayPathCommand = {
      type: "geometry/create-arraypath",
      source: command.source,
      entityIds: sourceIds,
      arrayGroupId,
      pathEntityId: command.path.pathEntityId,
      method: command.path.method,
      itemCount: command.path.itemCount,
      itemSpacing: command.path.itemSpacing,
      alignItems: command.path.alignItems,
    };
    return handleCreateArrayPath(withoutCopies, createCmd);
  }

  return doc;
}

/**
 * Utility — exposed for tests. Counts entities tagged with the given
 * arrayGroupId in the document (both sources and copies).
 */
export function countArrayGroupMembers(
  doc: DrawingDocument,
  groupId: string,
): { sources: number; copies: number; total: number } {
  let sources = 0;
  let copies = 0;
  for (const space of doc.spaces) {
    for (const entity of space.entities) {
      if (entity.arrayGroupId === groupId) {
        if (entity.arraySourceOfGroup === groupId) {
          sources += 1;
        } else {
          copies += 1;
        }
      }
    }
  }
  return { sources, copies, total: sources + copies };
}

/**
 * Utility — kept exported for tests. Computes the number of copies
 * produced by an ARRAYPATH command (excluding the first sample which
 * is the source's position).
 */
export function countArrayPathCopies(
  sampleCount: number,
  entityCount: number,
): number {
  return Math.max(0, sampleCount - 1) * entityCount;
}
